import { useMemo } from "react";

import { Observation } from "@medplum/fhirtypes";

import { Group, List, Table } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import ActionCell from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/ActionCell";
import LastUpdated from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/LastUpdated";
import PreviousVersions from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions";
import classes from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions.module.css";
import TimeCell from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/TimeCell";
import { hasPreviousVersions } from "@/encounters/components/EncounterLabor/services/labor";
import { getResuscitationValues } from "@/encounters/components/EncounterLabor/utils/labor";

interface ResuscitationRowProps {
  time: string;
  observation: Observation;
  showNumbersOnly: boolean;
  flowsheetId?: string;
  showPreviousVersions?: boolean;
  isViewOnly?: boolean;
}

export default function ResuscitationRow({
  time,
  observation,
  showNumbersOnly,
  flowsheetId,
  showPreviousVersions = true,
  isViewOnly = false,
}: ResuscitationRowProps) {
  const { procedures, birth, hasAnswers, hasNotes, notes } = useMemo(
    () => getResuscitationValues(observation),
    [observation],
  );

  const resuscitationItems = useMemo(() => {
    const items: Array<{ label: string; value: string }> = [];

    // Add procedures
    procedures.forEach((procedure) => {
      items.push({ label: `${procedure.name}:`, value: procedure.time });
    });

    // Add birth time if present
    if (birth) {
      items.push({ label: "Time of Birth:", value: birth });
    }

    return items;
  }, [procedures, birth]);

  if ((!hasAnswers && !hasNotes) || showNumbersOnly) return null;

  const previousVersions = hasPreviousVersions(observation);

  const editLaborStage = flowsheetId !== "clientPostpartum" && flowsheetId !== "newbornPostpartum";

  return (
    <Table.Tr bg="white" className={classes.tableRow}>
      {showPreviousVersions && (
        <TimeCell time={time} rowSpan={1} isViewOnly={isViewOnly} observation={observation} />
      )}
      <Table.Td p="md" colSpan={10}>
        {hasAnswers && (
          <>
            <TextWrapper size="sm">Resuscitation:</TextWrapper>
            <List fz="sm" withPadding>
              {resuscitationItems.map(
                (item, index) =>
                  item.value && (
                    <List.Item key={index}>
                      <Group gap="xs">
                        <TextWrapper size="sm" variant="bold">
                          {item.label}
                        </TextWrapper>
                        <TextWrapper size="sm">{item.value}</TextWrapper>
                      </Group>
                    </List.Item>
                  ),
              )}
            </List>
          </>
        )}
        {hasNotes && (
          <TextWrapper pt="xs" size="sm">
            Resuscitation Comments: {notes}
          </TextWrapper>
        )}
        {showPreviousVersions && previousVersions ? (
          <PreviousVersions
            observation={observation}
            rowComponent={ResuscitationRow}
            showPreviousVersions={showPreviousVersions}
          />
        ) : (
          <LastUpdated observation={observation} showPreviousVersions={false} />
        )}
      </Table.Td>
      {showPreviousVersions && editLaborStage && !isViewOnly && (
        <ActionCell rowSpan={1} observation={observation} />
      )}
    </Table.Tr>
  );
}
